import type { PermissionCode } from '@/lib/permissions'

import { apiClient } from './client'
import type {
  AssignUserRolesRequest,
  CreateRoleRequest,
  CreateUserRequest,
  DeviceListItem,
  DeviceListItemPayload,
  IssueTemporaryPasswordRequest,
  PermissionModuleGroup,
  ResetUserMfaRequest,
  RevokeDeviceRequest,
  RoleListItem,
  RoleListItemPayload,
  SetUserStatusRequest,
  TemporaryPassword,
  TemporaryPasswordPayload,
  UpdateRolePermissionsRequest,
  UpdateUserRequest,
  UserBranchGrant,
  UserBranchGrantPayload,
  UserDetail,
  UserDetailPayload,
  UserDeviceSummary,
  UserDeviceSummaryPayload,
  UserListItem,
  UserListItemPayload,
  UserPage,
  UserPagePayload,
  UserStatus,
} from './admin-types'
import { USER_STATUS_CODES } from './admin-types'
import type { DualControlResult } from './types'

/**
 * The Access module's administration endpoints: staff accounts, roles, the permission catalogue,
 * and the field devices on an account.
 *
 * Every write here that amends something already in force — a profile, a role assignment, an
 * account's status, a role's own permission set — answers with a {@link DualControlResult} and
 * never with the changed record. There is nothing to write into a cache on a request that may not
 * yet have happened; callers invalidate and re-read, the same discipline `groups/mutations.ts`
 * documents for the same reason.
 *
 * Two commands are deliberately not part of that pattern, and each is called out where it is
 * defined below: issuing a temporary password (200, a one-time secret, never an approval envelope)
 * and revoking a field device (204, despite `sync.device.revoke.approve` existing in the permission
 * catalogue).
 */

const toId = (value: number | string): string => String(value)
const toNumber = (value: number | string): number => (typeof value === 'number' ? value : Number(value))

const USER_STATUSES: Record<number, UserStatus> = {
  1: 'Active',
  2: 'Locked',
  3: 'Disabled',
}

function toUserListItem(payload: UserListItemPayload): UserListItem {
  return {
    userId: payload.userId,
    username: payload.username,
    fullName: payload.fullName,
    employeeCode: payload.employeeCode,
    branchId: toId(payload.branchId),
    status: USER_STATUSES[payload.status] ?? 'Unknown',
    isLoanOfficer: payload.isLoanOfficer,
    isTeller: payload.isTeller,
    mfaEnrolled: payload.mfaEnrolled,
    lockedOut: payload.lockedOut,
    lastLoginUtc: payload.lastLoginUtc,
    roles: Array.isArray(payload.roles) ? payload.roles : [],
  }
}

function toUserBranchGrant(payload: UserBranchGrantPayload): UserBranchGrant {
  return { branchId: toId(payload.branchId), expiresAtUtc: payload.expiresAtUtc }
}

function toUserDeviceSummary(payload: UserDeviceSummaryPayload): UserDeviceSummary {
  return {
    deviceId: payload.deviceId,
    deviceUid: payload.deviceUid,
    platform: payload.platform,
    status: payload.status,
    registeredAtUtc: payload.registeredAtUtc,
    lastSyncUtc: payload.lastSyncUtc,
  }
}

function toUserDetail(payload: UserDetailPayload): UserDetail {
  return {
    userId: payload.userId,
    username: payload.username,
    fullName: payload.fullName,
    employeeCode: payload.employeeCode,
    email: payload.email,
    phoneNumber: payload.phoneNumber,
    branchId: toId(payload.branchId),
    status: USER_STATUSES[payload.status] ?? 'Unknown',
    isLoanOfficer: payload.isLoanOfficer,
    isTeller: payload.isTeller,
    mustChangePassword: payload.mustChangePassword,
    mfaEnrolled: payload.mfaEnrolled,
    mfaEnrolledAtUtc: payload.mfaEnrolledAtUtc,
    remainingRecoveryCodes: toNumber(payload.remainingRecoveryCodes),
    lockedOut: payload.lockedOut,
    lockoutEndsAtUtc: payload.lockoutEndsAtUtc,
    failedAccessAttempts: toNumber(payload.failedAccessAttempts),
    lastLoginUtc: payload.lastLoginUtc,
    createdAtUtc: payload.createdAtUtc,
    roles: Array.isArray(payload.roles) ? payload.roles : [],
    // Trusted rather than narrowed against the catalogue: a permission this build does not
    // recognise still has to be visible on the one account that holds it, not silently dropped.
    permissions: (Array.isArray(payload.permissions) ? payload.permissions : []) as PermissionCode[],
    additionalBranches: Array.isArray(payload.additionalBranches)
      ? payload.additionalBranches.map(toUserBranchGrant)
      : [],
    devices: Array.isArray(payload.devices) ? payload.devices.map(toUserDeviceSummary) : [],
  }
}

export interface AdminUserListQuery {
  /** Free text — the endpoint does not document which fields it matches over. */
  search?: string
  branchId?: string
  status?: UserStatus
  page?: number
  pageSize?: number
}

export async function listUsers(query: AdminUserListQuery): Promise<UserPage> {
  const payload = (
    await apiClient.get<UserPagePayload>('/access/users', {
      params: {
        search: query.search,
        branchId: query.branchId,
        // The server takes the integer; `Unknown` never reaches a request because nothing offers
        // it as a filter choice.
        status: query.status && query.status !== 'Unknown' ? USER_STATUS_CODES[query.status] : undefined,
        page: query.page,
        pageSize: query.pageSize,
      },
    })
  ).data

  const items = Array.isArray(payload?.items) ? payload.items.map(toUserListItem) : []

  return {
    items,
    page: toNumber(payload?.page ?? query.page ?? 1) || 1,
    pageSize: toNumber(payload?.pageSize ?? query.pageSize ?? 25) || 25,
    totalCount: toNumber(payload?.totalCount ?? items.length),
  }
}

export async function getUser(userId: string): Promise<UserDetail> {
  const response = await apiClient.get<UserDetailPayload>(`/access/users/${encodeURIComponent(userId)}`)
  return toUserDetail(response.data)
}

/**
 * Creates a staff account. Subject to dual control and issues no credential — see
 * `temporary-password-dialog.tsx` for the separate, single-use call that does.
 */
export async function createUser(request: CreateUserRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/access/users', request)
  return response.data
}

export async function updateUser(userId: string, request: UpdateUserRequest): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/access/users/${encodeURIComponent(userId)}`,
    request,
  )
  return response.data
}

/**
 * Locks, unlocks, or disables an account. Only unlocking is dual-controlled — `admin.user.unlock`
 * is the sole one of the three maker permissions with a `.approve` counterpart in the catalogue, and
 * `user-account-actions.tsx` reads `result.awaitingApproval` rather than assuming which transition
 * it was given.
 */
export async function setUserStatus(
  userId: string,
  request: SetUserStatusRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/access/users/${encodeURIComponent(userId)}/status`,
    { status: USER_STATUS_CODES[request.status as Exclude<UserStatus, 'Unknown'>], reason: request.reason },
  )
  return response.data
}

/**
 * Clears a user's authenticator enrolment. Dual-controlled — `admin.mfa.reset.approve` gates the
 * countersignature.
 */
export async function resetUserMfa(
  userId: string,
  request: ResetUserMfaRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/access/users/${encodeURIComponent(userId)}/mfa/reset`,
    request,
  )
  return response.data
}

/**
 * Issues a one-time password. Answers with the password itself (200) rather than an approval
 * envelope — this is the one account-maintenance command in this module the catalogue does not
 * dual-control, and there is no dedicated permission code for it either; the screen gates the
 * button on `admin.user.update`, the closest generated code, and the server is the authority on
 * whether that is actually what it checks.
 */
export async function issueTemporaryPassword(
  userId: string,
  request: IssueTemporaryPasswordRequest,
): Promise<TemporaryPassword> {
  const response = await apiClient.post<TemporaryPasswordPayload>(
    `/access/users/${encodeURIComponent(userId)}/password`,
    request,
  )
  return response.data
}

/**
 * Replaces a user's roles, enforcing segregation of duties server-side. Dual-controlled under
 * `admin.role.assign` / `admin.role.assign.approve` — a different permission pair from
 * `admin.role.manage`, which governs a role's own definition rather than who holds it.
 */
export async function assignUserRoles(
  userId: string,
  request: AssignUserRolesRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/access/users/${encodeURIComponent(userId)}/roles`,
    request,
  )
  return response.data
}

/* ─── Roles and the permission catalogue ─────────────────────────────────────────────────── */

function toRoleListItem(payload: RoleListItemPayload): RoleListItem {
  return {
    roleId: payload.roleId,
    name: payload.name,
    description: payload.description,
    isSystemRole: payload.isSystemRole,
    forbidsFinancialPermissions: payload.forbidsFinancialPermissions,
    // A build talking to a server from before the field existed reads `undefined`; treating that
    // as break-glass would silently hide roles from the create form, so absent means false.
    isBreakGlass: payload.isBreakGlass === true,
    userCount: toNumber(payload.userCount),
    permissions: (Array.isArray(payload.permissions) ? payload.permissions : []) as PermissionCode[],
  }
}

/**
 * Every role, with its permission set and how many users hold it.
 *
 * There is no `GET /access/roles/{roleId}` — this is the only read the contract publishes, so a
 * role's detail screen finds its row in this same array rather than fetching one of its own. See
 * `role-detail.tsx`.
 */
export async function listRoles(): Promise<RoleListItem[]> {
  const response = await apiClient.get<RoleListItemPayload[]>('/access/roles')
  return Array.isArray(response.data) ? response.data.map(toRoleListItem) : []
}

/**
 * Defines a new role. Subject to dual control. Implemented for completeness of this module's API
 * coverage; no screen calls it in this pass — role administration here covers listing an existing
 * role, reading its permissions, and amending them, not defining a new one from scratch.
 */
export async function createRole(request: CreateRoleRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/access/roles', request)
  return response.data
}

export async function updateRolePermissions(
  roleId: string,
  request: UpdateRolePermissionsRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/access/roles/${encodeURIComponent(roleId)}/permissions`,
    request,
  )
  return response.data
}

/** The permission catalogue, grouped by module — what a role's permission editor is built from. */
export async function listPermissions(): Promise<PermissionModuleGroup[]> {
  const response = await apiClient.get<PermissionModuleGroup[]>('/access/permissions')
  return Array.isArray(response.data) ? response.data : []
}

/* ─── Devices, across the whole directory ────────────────────────────────────────────────── */

function toDeviceListItem(payload: DeviceListItemPayload): DeviceListItem {
  return {
    deviceId: payload.deviceId,
    userId: toId(payload.userId),
    username: payload.username,
    deviceUid: payload.deviceUid,
    platform: payload.platform,
    status: payload.status,
    model: payload.model,
    appVersion: payload.appVersion,
    registeredAtUtc: payload.registeredAtUtc,
    lastSyncUtc: payload.lastSyncUtc,
    revokedReason: payload.revokedReason,
  }
}

/**
 * Every registered field device, optionally filtered to one user. Implemented for completeness;
 * no screen browses the cross-user list in this pass — a user's own devices are already on
 * {@link UserDetail}, which is where `user-account-actions.tsx` revokes one from, and which needs
 * none of this endpoint's mismatched `userId` (see `admin-types.ts`).
 */
export async function listDevices(params: {
  userId?: string
  includeRevoked?: boolean
}): Promise<DeviceListItem[]> {
  const response = await apiClient.get<DeviceListItemPayload[]>('/access/devices', { params })
  return Array.isArray(response.data) ? response.data.map(toDeviceListItem) : []
}

/**
 * Revokes a field device. Answers 204 — plain success, not an approval envelope — even though the
 * permission catalogue names `sync.device.revoke.approve`. Either the countersignature is reserved
 * for a future pass or this particular command does not implement `IApprovableCommand` yet; either
 * way, there is no envelope for a screen to read, so this is treated as an ordinary action rather
 * than one that might be queued. The button is gated on `sync.device.revoke` alone.
 */
export async function revokeDevice(deviceId: string, request: RevokeDeviceRequest): Promise<void> {
  await apiClient.post(`/access/devices/${encodeURIComponent(deviceId)}/revoke`, request)
}
